import { useEffect, useState } from 'react';
import Link from 'next/link';
import { cn } from 'ui/lib/utils';
import { getNewArrivals } from '@util/firestore/products';
import { getProductUrl } from '@util/urlHelpers';

type EventType = 'listed' | 'dropped' | 'sold';

/**
 * How the band presents itself, decided by the data rather than assumed.
 *
 * 'live'   — every rendered item really was listed in the last 24h, so the LIVE
 *            badge, the "just listed" verb and the relative timestamps are all
 *            literally true.
 * 'recent' — not enough fresh listings to earn that framing. Same listings,
 *            but the band claims nothing about time: no LIVE badge, no pulse,
 *            no temporal verb, no timestamps. Shows the price instead, which is
 *            both useful and unconditionally true.
 *
 * The previous version had one mode and always claimed the first. timeAgo()
 * returned '' past 24h, which removed the EVIDENCE of staleness while leaving
 * the claim — every item on the preview was older than a day and every one of
 * them read "just listed" under a pulsing LIVE badge.
 */
type TickerMode = 'live' | 'recent';

interface TickerItem {
  type: EventType;
  title: string;
  /** Empty in 'recent' mode for plain listings — nothing true to say. */
  verb: string;
  /** Relative age in 'live' mode, price in 'recent' mode. */
  meta: string;
  href: string;
}

const FRESH_WINDOW_S = 86_400;
/** Below this many genuinely-fresh listings the LIVE framing isn't earned. */
const MIN_LIVE_ITEMS = 6;
const MAX_ITEMS = 16;
/** Fetch wider than we render so a 24h filter still has a full band to work
 *  with on a normal day, and degrades to 'recent' only when velocity is low. */
const FETCH_LIMIT = 32;

const dotColor: Record<EventType, string> = {
  listed: 'bg-primary',
  dropped: 'bg-warning',
  sold: 'bg-success',
};

const truncate = (t: string, n = 40) =>
  t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t;

/** Age in seconds, or null when there's no usable timestamp. */
function ageSeconds(ts?: number): number | null {
  if (!ts) return null;
  const ms = ts < 1e12 ? ts * 1000 : ts; // seconds -> ms
  return Math.max(0, Math.floor((Date.now() - ms) / 1000));
}

/** Only ever called on items already known to be inside FRESH_WINDOW_S. */
function timeAgo(s: number): string {
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

const formatPrice = (n: number) => `$${Math.round(n).toLocaleString()}`;

/**
 * Ported from gearfocus-v2's marketplace LiveTicker (dark variant) wrapped in a
 * full-bleed dark band. Populated from real listings (getNewArrivals). Seamless
 * scroll via the shared gf-marquee keyframe.
 *
 * The band earns its LIVE framing from the data — see TickerMode. It renders in
 * both modes rather than hiding itself when listings are thin: a homepage
 * section that appears and disappears with marketplace velocity is its own
 * problem (layout instability, and an inconsistent page between visits), and
 * 'recent' mode presents exactly the same listings without making a claim about
 * when they arrived.
 */
const LiveActivityBar = ({
  className,
  sticky = false,
}: {
  className?: string;
  /** Pin the bar just below the sticky header once it's scrolled to. */
  sticky?: boolean;
}) => {
  const [items, setItems] = useState<TickerItem[]>([]);
  const [mode, setMode] = useState<TickerMode>('recent');
  // Explicit, rather than inferred from `items.length === 0`. That inference
  // cannot tell "still fetching" from "fetch failed" or "nothing came back",
  // so a rejected request or an empty result left the skeleton animating
  // forever. Set in .finally(), which runs on both paths.
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getNewArrivals(FETCH_LIMIT)
      .then((res) => {
        if (cancelled || !res?.results) return;
        const usable = res.results.filter((p) => p.title && p.slug);

        // Decide the framing from the data, then render only what that framing
        // can honestly describe. In 'live' mode the stale items are dropped
        // outright rather than shown with the timestamp hidden.
        const fresh = usable.filter((p) => {
          const age = ageSeconds(p.date_added);
          return age !== null && age <= FRESH_WINDOW_S;
        });
        const live = fresh.length >= MIN_LIVE_ITEMS;
        const source = (live ? fresh : usable).slice(0, MAX_ITEMS);

        setMode(live ? 'live' : 'recent');
        setItems(
          source.map((p): TickerItem => {
            const isDrop = p.on_sale && p.og_price > p.price;
            return {
              type: isDrop ? 'dropped' : 'listed',
              title: truncate(p.title, 40),
              // 'on sale' is a current state, so it stays true in either mode;
              // 'just listed' is a claim about time and is only made when the
              // item is provably inside the fresh window.
              verb: isDrop ? (live ? 'price drop' : 'on sale') : live ? 'just listed' : '',
              meta: live
                ? timeAgo(ageSeconds(p.date_added) as number)
                : formatPrice(p.price),
              href: getProductUrl(p.slug),
            };
          })
        );
      })
      .catch((e) => console.error('LiveActivityBar: failed to load listings', e))
      .finally(() => {
        if (!cancelled) setSettled(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loading = !settled;
  const duplicated = loading ? [] : [...items, ...items];

  // Settled with nothing to say: render no bar at all rather than an empty
  // strip. Previously unreachable — `loading` was derived from an empty
  // `items`, so this state was indistinguishable from still-fetching and the
  // skeleton just kept animating.
  if (settled && items.length === 0) return null;

  return (
    <div
      data-slot="live-ticker"
      // Deliberately no role and no aria-live. The whole list arrives in one
      // state update, so `role="status"` made screen readers announce all
      // sixteen listings back to back. The marquee is decorative; its links are
      // reachable in the tab order like any other row of links, and the
      // duplicated set used for the seamless scroll is already aria-hidden.
      // (An aria-label here would be dropped anyway — it needs a role to be
      // exposed.)
      // When sticky, pin just below the header. --gf-header-h is published by
      // Navbar and tracks its scroll-collapse, so the bar sits flush under the
      // menu (same pattern as StickyTabs). No inline fallback: globals.css
      // carries a responsive default for the pre-observer frame. z-40 keeps it
      // above page content but below the header (z-sticky) and its mega-menu.
      className={cn('w-full bg-dark', sticky && 'sticky z-40', className)}
      style={sticky ? { top: 'var(--gf-header-h)' } : undefined}
    >
      {/* Full page width (no max-w cap) so the ticker spans edge-to-edge like
          the brand marquee; the LIVE label keeps the page's left gutter. */}
      <div className="flex w-full items-center overflow-hidden">
        <div className="z-10 flex h-[40px] shrink-0 items-center gap-[8px] bg-dark pl-[12px] pr-[16px]">
          {/* The pulse is a liveness signal, so it only runs when the band is
              actually showing live data. */}
          <span
            aria-hidden="true"
            className={cn(
              'inline-block size-[8px] rounded-full bg-primary',
              mode === 'live' && 'animate-pulse'
            )}
          />
          <span className="text-[12px] font-bold uppercase tracking-wide text-white">
            {mode === 'live' ? 'Live' : 'New in'}
          </span>
        </div>
        {/* Fade on BOTH edges: items fade in on the right and fade out on the
            left as they pass behind the LIVE label. */}
        <div className="relative flex-1 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
        <div
          className={cn(
            'flex w-max items-center gap-[40px] py-[8px]',
            !loading && 'animate-gf-marquee hover:[animation-play-state:paused]'
          )}
          style={loading ? undefined : { animationDuration: '100s' }}
        >
          {loading &&
            Array.from({ length: 8 }).map((_, i) => (
              <div
                key={`skeleton-${i}`}
                className="inline-flex shrink-0 items-center gap-[8px] px-[4px]"
              >
                <span className="inline-block size-[8px] rounded-full bg-white/20" />
                <span className="block h-[12px] w-[170px] animate-pulse rounded bg-white/10" />
              </div>
            ))}
          {duplicated.map((item, i) => {
            const clone = i >= items.length;
            return (
              <Link
                key={`${item.title}-${i}`}
                href={item.href}
                aria-hidden={clone || undefined}
                tabIndex={clone ? -1 : undefined}
                className="inline-flex shrink-0 items-center gap-[8px] px-[4px] text-[14px]"
              >
                <span
                  aria-hidden="true"
                  className={cn('inline-block size-[8px] rounded-full', dotColor[item.type])}
                />
                <span className="font-semibold text-white">
                  {item.title}
                  {item.verb && (
                    <>
                      {' '}
                      <span className="font-normal text-gray-400">{item.verb}</span>
                    </>
                  )}
                </span>
                {item.meta && (
                  <>
                    <span aria-hidden="true" className="text-white/30">
                      ·
                    </span>
                    <span className="font-mono text-[12px] tabular-nums text-gray-500">
                      {item.meta}
                    </span>
                  </>
                )}
              </Link>
            );
          })}
        </div>
        </div>
      </div>
    </div>
  );
};

export default LiveActivityBar;
